import {
  CalendarIcon,
  CheckIcon,
  LeafyGreenIcon,
  MinusIcon,
  PenSquareIcon,
  PlusIcon,
  Snowflake,
  XIcon,
} from 'lucide-react'
import { Fragment, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import useSWR from 'swr'

import RecipeImageCredit from '../components/RecipeImageCredit'
import { useSheetHeaderActions } from '../components/Sheet'
import { useMe } from '../lib/auth-client'
import {
  calculateIngredientQuantities,
  shouldDisplayIngredient,
} from '../lib/ingredient'
import { useRouter } from '../lib/navigation'
import { api } from '../lib/password'
import {
  toggleRecipeImageSize,
  useRecipeImageSize,
} from '../lib/recipeImageSizePrefs'
import { fetcher } from '../lib/swr'
import { useRelativeDate } from '../lib/useRelativeDate'
import RecipeEditor from './RecipeEditor'
import type { FullRecipe, Ingredient, Plan, Product } from './types'

export default function PlanDetail(props: {
  plan?: Plan & { recipe?: FullRecipe | null }
  planId?: number
  recipe?: FullRecipe
  recipeId?: number
  initialEditing?: boolean
  onChange: () => void
  onClose?: () => void
  onRecipeIdChange?: (recipeId: number) => void
  onEditingChange?: (editing: boolean) => void
}) {
  // Load fresh data
  const swr = useSWR<Plan & { recipe: FullRecipe }>(
    !props.plan && props.planId ? '/api/plans/' + props.planId : null,
    fetcher
  )
  const passedRecipe = props.recipe || props.plan?.recipe
  const swrRecipe = useSWR<FullRecipe>(
    props.recipeId && (!passedRecipe || !passedRecipe.tenantName)
      ? '/api/recipes/' + props.recipeId
      : null,
    fetcher
  )

  // Decide current data
  const plan = swr.data || props.plan
  const fetchedRecipe =
    swrRecipe.data && swrRecipe.data.id === props.recipeId
      ? swrRecipe.data
      : undefined
  const recipe =
    swr.data?.recipe ||
    (passedRecipe?.tenantName ? passedRecipe : undefined) ||
    fetchedRecipe ||
    passedRecipe

  const date = useRelativeDate(plan?.plannedAt)

  return (
    <RecipeDetail
      key={plan?.id || props.planId || props.recipeId}
      recipe={recipe ?? undefined}
      plan={plan}
      date={date}
      initialEditing={props.initialEditing}
      onClose={props.onClose}
      onRecipeIdChange={props.onRecipeIdChange}
      onEditingChange={props.onEditingChange}
      onChange={() => {
        if (swr.data) swr.mutate()
        if (swrRecipe.data) swrRecipe.mutate()
        props.onChange()
      }}
      mutateRecipe={(next) => {
        if (swrRecipe.data) swrRecipe.mutate(next, { revalidate: false })
      }}
    />
  )
}

function RecipeDetail({
  recipe,
  plan,
  date,
  initialEditing,
  onChange,
  onClose,
  onRecipeIdChange,
  onEditingChange,
  mutateRecipe,
}: {
  recipe?: FullRecipe
  plan?: Plan
  date: string
  initialEditing?: boolean
  onChange: () => void
  onClose?: () => void
  onRecipeIdChange?: (recipeId: number) => void
  onEditingChange?: (editing: boolean) => void
  mutateRecipe: (recipe: FullRecipe) => void
}) {
  const router = useRouter()
  const { data: me } = useMe()
  const tenantId = me?.user?.tenantId ?? null
  const signedIn = Boolean(tenantId)

  const [state, setState] = useState<'' | 'x' | 'doing' | 'done'>('')
  const [editing, setEditing] = useState(Boolean(initialEditing))
  const [startingEdit, setStartingEdit] = useState(false)
  const [override, setOverride] = useState<FullRecipe | null>(null)
  const [, tick] = useState(false)
  const [imageFailed, setImageFailed] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const consumedAtRef = useRef<HTMLInputElement>(null)
  const imageSize = useRecipeImageSize()
  const imageSmall = imageSize === 'small'

  const current =
    override && (!recipe || override.id === recipe.id || editing)
      ? override
      : recipe

  useEffect(() => {
    setImageFailed(false)
    setImageLoaded(false)
  }, [current?.id])

  const owned =
    current != null && tenantId != null && current.tenantId === tenantId

  const setEditMode = (next: boolean) => {
    setEditing(next)
    onEditingChange?.(next)
  }

  const applyRecipe = (next: FullRecipe) => {
    setOverride(next)
    mutateRecipe(next)
  }

  const setConsumedAt = async (consumedAt: string | null) => {
    if (!plan) return
    setState('doing')
    try {
      const ok = await api.patch('/api/plans/' + plan.id, {
        consumedAt: consumedAt
          ? consumedAt.slice(0, 10) + 'T12:00:00.000Z'
          : null,
      })
      if (ok.data?.message) {
        toast.error(String(ok.data.message))
        setState('')
        return
      }
      setState(consumedAt ? 'done' : '')
      onChange()
      if (consumedAt) close()
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : consumedAt
            ? 'Kon niet markeren als klaar'
            : 'Kon bereidingsdatum niet wissen'
      )
      setState('')
    }
  }

  const setPlannedAt = async (plannedAt: string | null) => {
    if (!plan) return
    setState('doing')
    try {
      await api.patch('/api/plans/' + plan.id, {
        plannedAt: plannedAt ? plannedAt.slice(0, 10) + 'T12:00:00.000Z' : null,
      })
      setState('')
      onChange()
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : plannedAt
            ? 'Plannen mislukt'
            : 'Kon plantdatum niet wissen'
      )
      setState('')
    }
  }

  const openConsumedPicker = () => {
    const el = consumedAtRef.current
    if (!el || el.disabled) return
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker()
        return
      }
    } catch {
      // Fall through — some webviews block showPicker.
    }
    el.focus()
    el.click()
  }

  const close = () => {
    if (onClose) return onClose()
    router.back()
  }

  const setMultiplier = async (multiplier: number) => {
    if (multiplier < 0) return setMultiplierFreezer(multiplier)
    if (plan) {
      plan.multiplier = multiplier
    }
    tick((t) => !t)
    const ok = await api.patch('/api/plans/' + plan!.id, { multiplier })
    if (ok.data.message) alert(ok.data.message)
    onChange()
  }

  const setMultiplierFreezer = async (multiplierFreezer: number) => {
    if (plan) {
      if (plan.multiplierFreezer! < 0 && multiplierFreezer === 0) {
        plan.multiplier = 0
        plan.multiplierFreezer = 0
      } else {
        plan.multiplierFreezer = multiplierFreezer
        if (multiplierFreezer < 0) plan.multiplier = -multiplierFreezer
      }
    }
    tick((t) => !t)
    const ok = await api.patch('/api/plans/' + plan!.id, {
      multiplierFreezer: plan?.multiplierFreezer,
      multiplier: plan?.multiplier,
    })
    if (ok.data.message) alert(ok.data.message)
    onChange()
  }

  const startEdit = async () => {
    if (!current || !signedIn) return
    if (owned) {
      setEditMode(true)
      return
    }
    if (
      !confirm(
        'Dit recept hoort bij een ander huishouden.\nWe maken een kopie die jij kunt bewerken.'
      )
    ) {
      return
    }
    setStartingEdit(true)
    try {
      const { data } = await api.post<FullRecipe>(
        `/api/recipes/${current.id}/copy`
      )
      if (plan) {
        await api.patch(`/api/plans/${plan.id}`, { recipeId: data.id })
      }
      toast.success('Kopie gemaakt in jouw huishouden')
      onRecipeIdChange?.(data.id)
      applyRecipe(data)
      onChange()
      setEditMode(true)
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Kopiëren mislukt'
      toast.error(message)
    } finally {
      setStartingEdit(false)
    }
  }

  const editLabel = startingEdit
    ? 'Kopie maken…'
    : owned
      ? 'Bewerk recept'
      : 'Bewerk als kopie'

  useSheetHeaderActions(
    current && signedIn && !editing ? (
      <button
        type="button"
        disabled={startingEdit}
        onClick={() => void startEdit()}
        className="Sheet-headerAction"
        title={editLabel}
        aria-label={editLabel}
      >
        <PenSquareIcon className="h-5 w-5" />
      </button>
    ) : null,
    [current?.id, signedIn, editing, startingEdit, owned, editLabel]
  )

  const freezerMultiplier = plan?.multiplierFreezer ?? 1
  const eatMultiplier = plan?.multiplier ?? 1

  const portionsFreezer = current?.portionsFreezer ?? 0
  const portionsEat = current?.portionsEat ?? 2

  if (editing && current) {
    return (
      <RecipeEditor
        recipe={current}
        onCancel={() => setEditMode(false)}
        onSaved={(next) => {
          applyRecipe(next)
          setEditMode(false)
          onChange()
        }}
        onImageChange={(hasImage, imageSource, attribution) => {
          applyRecipe({
            ...current,
            hasImage,
            imageSource: imageSource ?? null,
            imagePhotographer: attribution?.photographer ?? null,
            imagePhotographerUrl: attribution?.photographerUrl ?? null,
            imagePageUrl: attribution?.pageUrl ?? null,
          })
        }}
        onArchived={() => {
          setEditMode(false)
          onChange()
          close()
        }}
      />
    )
  }

  const showRecipeImage =
    Boolean(current) &&
    !imageFailed &&
    (Boolean(current?.hasImage) || imageLoaded)
  // Only credit when API says so — never invent TheMealDB for user uploads
  // or slim history payloads that omit imageSource.
  const imageCreditSource =
    current?.imageSource === 'pexels' || current?.imageSource === 'themealdb'
      ? current.imageSource
      : null

  const onRecipeImageLoad = () => {
    setImageLoaded(true)
    if (!current || current.hasImage || current.imageSource) return
    // MealDB auto-cache may have just filled the row; refresh attribution.
    void fetch(`/api/recipes/${current.id}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((r: FullRecipe | null) => {
        if (!r || r.id !== current.id) return
        applyRecipe({
          ...current,
          hasImage: Boolean(r.hasImage),
          imageSource: r.imageSource ?? null,
          imagePhotographer: r.imagePhotographer ?? null,
          imagePhotographerUrl: r.imagePhotographerUrl ?? null,
          imagePageUrl: r.imagePageUrl ?? null,
        })
      })
      .catch(() => {})
  }

  return (
    <>
      <div className="opacity-60 pr-20 mb-2">{date}&nbsp;</div>
      <div
        className={
          showRecipeImage && imageSmall ? 'flex items-start gap-3' : undefined
        }
      >
        {showRecipeImage && imageSmall && current ? (
          <button
            type="button"
            title="Klik voor grotere afbeelding"
            aria-label="Afbeeldingsgrootte wisselen"
            onClick={() => toggleRecipeImageSize()}
            className="mt-1 shrink-0 overflow-hidden rounded-lg ring-1 ring-black/10 outline-none focus-visible:ring-2 focus-visible:ring-lime-300"
          >
            <img
              src={`/api/recipes/${current.id}/image`}
              alt=""
              className="block h-[50px] w-[50px] object-cover"
              onError={() => setImageFailed(true)}
              onLoad={onRecipeImageLoad}
            />
          </button>
        ) : null}
        <h2
          className="min-w-0 flex-1 font-bold text-3xl lg:text-4xl xl:text-5xl pr-20"
          onClick={async () => {
            if (!plan) return
            const display =
              prompt(
                `Nieuwe naam voor dit plan:` +
                  (current?.display
                    ? `\nLeeglaten om "${current?.display}" te gebruiken.`
                    : ''),
                plan?.display || current?.display || ''
              ) || ''
            await api.patch('/api/plans/' + plan.id, { display })
            onChange()
          }}
        >
          {plan?.display || current?.display || 'Plan een gerecht...'}
        </h2>
      </div>

      {current && !owned && current.tenantId != null ? (
        <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-amber-100 text-amber-900 ring-1 ring-amber-200/80 px-2 py-0.5 text-xs font-medium">
          {current.tenantHasImage ? (
            <img
              src={`/api/tenants/${current.tenantId}/image`}
              alt=""
              className="size-4 rounded-full object-cover ring-1 ring-amber-300/60"
            />
          ) : null}
          {current.tenantName || 'Ander huishouden'}
        </div>
      ) : null}

      {showRecipeImage && !imageSmall && current ? (
        <div className="mt-5">
          <button
            type="button"
            title="Klik voor kleinere afbeelding"
            aria-label="Afbeeldingsgrootte wisselen"
            onClick={() => toggleRecipeImageSize()}
            className="block w-full overflow-hidden rounded-2xl ring-1 ring-black/5 bg-stone-100 aspect-[16/9] outline-none focus-visible:ring-2 focus-visible:ring-lime-300"
          >
            <img
              src={`/api/recipes/${current.id}/image`}
              alt=""
              className="h-full w-full object-cover"
              onError={() => setImageFailed(true)}
              onLoad={onRecipeImageLoad}
            />
          </button>
          {imageCreditSource || current.imagePhotographer?.trim() ? (
            <RecipeImageCredit
              source={imageCreditSource}
              photographer={current.imagePhotographer}
              photographerUrl={current.imagePhotographerUrl}
              pageUrl={current.imagePageUrl}
            />
          ) : null}
        </div>
      ) : null}
      {showRecipeImage &&
      imageSmall &&
      current &&
      (imageCreditSource || current.imagePhotographer?.trim()) ? (
        <RecipeImageCredit
          source={imageCreditSource}
          photographer={current.imagePhotographer}
          photographerUrl={current.imagePhotographerUrl}
          pageUrl={current.imagePageUrl}
        />
      ) : null}
      {current && !current.hasImage && !imageLoaded && !imageFailed ? (
        <img
          src={`/api/recipes/${current.id}/image`}
          alt=""
          className="hidden"
          onLoad={onRecipeImageLoad}
          onError={() => setImageFailed(true)}
        />
      ) : null}

      {current?.preparationMinutes ? (
        <div className="text-sm text-lime-800 mt-4">
          ⏱️ Bereidingstijd: {current.preparationMinutes} minuten
        </div>
      ) : null}

      {plan && plan.multiplierFreezer && plan.multiplierFreezer < 0 ? (
        <div className="flex flex-col items-start">
          <div className="opacity-60 mt-6 mb-1">Uit de diepvries halen</div>
          <FreezerMultiplierInput
            value={-plan.multiplierFreezer}
            onChange={(x) => setMultiplierFreezer(-x)}
            recipe={current}
            invert
          />
        </div>
      ) : (
        plan && (
          <div className="flex gap-8">
            {typeof current?.portionsEat === 'number' && (
              <div>
                <div className="opacity-60 mt-6 mb-1">Vers eten</div>
                <MultiplierInput
                  value={plan.multiplier}
                  onChange={setMultiplier}
                  portionsEat={current?.portionsEat}
                />
              </div>
            )}
            {current?.ingredients?.some((i) => i.freezer) && (
              <div>
                <div className="opacity-60 mt-6 mb-1">Invriezen</div>
                <FreezerMultiplierInput
                  value={plan.multiplierFreezer}
                  onChange={setMultiplierFreezer}
                  recipe={current}
                />
              </div>
            )}
          </div>
        )
      )}

      {current?.ingredients?.length ? (
        <>
          <div className="opacity-60 mt-6">Ingrediënten</div>
          <div className="text-lg flex flex-col gap-2 my-2">
            {current.ingredients.map((ingredient) => (
              <IngredientLine
                key={ingredient.id}
                ingredient={ingredient}
                product={ingredient.product}
                multiplierEat={eatMultiplier}
                multiplierFreezer={freezerMultiplier}
                portionsEat={portionsEat}
                portionsFreezer={portionsFreezer}
              />
            ))}
          </div>
        </>
      ) : null}

      {current?.instructions ? (
        <>
          <div className="opacity-60 mt-6">Instructies</div>

          <Instructions instructions={current?.instructions} />
        </>
      ) : null}

      <div className="mb-2 mt-6 opacity-60">Acties</div>
      {plan && (
        <div className="mb-2 flex flex-col gap-3 text-sm font-medium">
          {(typeof plan.order === 'number' || plan.consumedAt) && (
            <div className="flex flex-wrap items-center gap-1">
              <div
                className={
                  (state ? 'text-gray-200' : 'text-lime-800') +
                  (!plan.consumedAt ? ' flex-1' : '') +
                  ' flex min-w-0 whitespace-nowrap items-center gap-2 rounded-full py-2 pl-2 pr-2 bg-lime-100'
                }
              >
                <button
                  type="button"
                  disabled={!!state}
                  onClick={openConsumedPicker}
                  className={
                    (state ? '' : 'hover:underline') +
                    ' flex flex-1 min-w-0 items-center gap-2 focus-visible:underline focus:outline-hidden text-left'
                  }
                >
                  <CheckIcon className="w-6 h-6 shrink-0" />
                  <div className="grow">Klaar met koken</div>
                </button>
                <input
                  ref={consumedAtRef}
                  disabled={!!state}
                  id={'consumedAt' + plan.id}
                  type="date"
                  className={
                    (state ? 'text-gray-200' : 'text-lime-800') +
                    ' ml-0 bg-transparent'
                  }
                  value={plan.consumedAt?.slice(0, 10) ?? ''}
                  onChange={(e) => {
                    const next = e.target.value
                    if (!next) return
                    void setConsumedAt(next)
                  }}
                  max={new Date().toJSON().slice(0, 10)}
                />
              </div>
              {!plan.consumedAt ? (
                <>
                  <button
                    type="button"
                    disabled={!!state}
                    className={
                      (state
                        ? 'text-gray-200'
                        : 'text-lime-800 focus-visible:underline hover:underline') +
                      ' block px-3 focus:outline-hidden bg-lime-100 h-10 rounded-full'
                    }
                    onClick={() =>
                      void setConsumedAt(
                        new Date(Date.now() - 24 * 36e5).toJSON()
                      )
                    }
                  >
                    Gisteren
                  </button>
                  <button
                    type="button"
                    disabled={!!state}
                    className={
                      (state
                        ? 'text-gray-200'
                        : 'text-lime-800 focus-visible:underline hover:underline') +
                      ' block px-3 focus:outline-hidden bg-lime-100 h-10 rounded-full'
                    }
                    onClick={() => void setConsumedAt(new Date().toJSON())}
                  >
                    Vandaag
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  disabled={!!state}
                  aria-label="Verwijder bereidingsdatum"
                  title="Verwijder bereidingsdatum"
                  className={
                    (state ? 'text-gray-200' : 'text-gray-500 hover:underline') +
                    ' flex items-center justify-center focus:outline-hidden bg-gray-100 size-10 rounded-full'
                  }
                  onClick={() => void setConsumedAt(null)}
                >
                  <XIcon className="w-6 h-6" />
                </button>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1">
            <label
              className={
                (!plan.plannedAt ? 'flex-1 ' : '') +
                'flex min-w-0 items-center gap-3 bg-lime-100 h-10 rounded-full pr-2'
              }
            >
              <div
                className={
                  (state ? 'text-gray-200' : 'text-lime-800') +
                  ' flex items-center gap-2 focus-visible:underline focus:outline-hidden pl-2 shrink-0'
                }
              >
                <CalendarIcon className="w-6 h-6" />
                Plannen op
              </div>

              <input
                type="date"
                min={new Date().toJSON().slice(0, 10)}
                disabled={!!state}
                className={
                  (state ? 'text-gray-200' : 'text-lime-800') +
                  ' ml-auto text-sm font-medium text-right bg-transparent min-w-0'
                }
                value={plan.plannedAt?.slice(0, 10) ?? ''}
                onChange={(e) => {
                  void setPlannedAt(e.target.value || null)
                }}
              />
            </label>
            {!plan.plannedAt ? (
              <>
                <button
                  type="button"
                  disabled={!!state}
                  className={
                    (state
                      ? 'text-gray-200'
                      : 'text-lime-800 focus-visible:underline hover:underline') +
                    ' block px-3 focus:outline-hidden bg-lime-100 h-10 rounded-full'
                  }
                  onClick={() =>
                    void setPlannedAt(new Date(Date.now() + 24 * 36e5).toJSON())
                  }
                >
                  Morgen
                </button>
                <button
                  type="button"
                  disabled={!!state}
                  className={
                    (state
                      ? 'text-gray-200'
                      : 'text-lime-800 focus-visible:underline hover:underline') +
                    ' block px-3 focus:outline-hidden bg-lime-100 h-10 rounded-full'
                  }
                  onClick={() =>
                    void setPlannedAt(
                      new Date(Date.now() + 2 * 24 * 36e5).toJSON()
                    )
                  }
                >
                  Overmorgen
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={!!state}
                aria-label="Verwijder plantdatum"
                title="Verwijder plantdatum"
                className={
                  (state ? 'text-gray-200' : 'text-gray-500 hover:underline') +
                  ' flex items-center justify-center focus:outline-hidden bg-gray-100 size-10 rounded-full'
                }
                onClick={() => void setPlannedAt(null)}
              >
                <XIcon className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>
      )}
      {plan && <div className="mt-6 opacity-60">Gevarenzone</div>}
      <div className="my-2 flex flex-col gap-4 items-start">
        {plan && (
          <button
            disabled={!!state}
            className={
              (state ? 'text-gray-200' : 'text-red-800 hover:underline') +
              ' flex items-center gap-2 text-sm font-medium focus-visible:underline focus:outline-hidden bg-red-50 py-2 pl-2 pr-4 rounded-full'
            }
            onClick={async () => {
              const fromHistory = Boolean(plan.consumedAt)
              if (
                !confirm(
                  fromHistory
                    ? 'Dit recept uit je geschiedenis verwijderen? Dit kan niet ongedaan worden gemaakt.'
                    : 'Ben je zeker?'
                )
              ) {
                return
              }
              setState('doing')
              try {
                await api.delete('/api/plans/' + plan.id)
                setState('')
                onChange()
                close()
              } catch (err) {
                toast.error(
                  err instanceof Error ? err.message : 'Verwijderen mislukt'
                )
                setState('')
              }
            }}
          >
            <XIcon className="w-6 h-6" />
            {plan.consumedAt
              ? 'Verwijderen uit geschiedenis'
              : 'Verwijderen uit planning'}
          </button>
        )}
      </div>
    </>
  )
}

function num(value: number) {
  return value.toLocaleString('nl-BE', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  })
}

const IngredientLine = ({
  ingredient,
  product,
  multiplierEat = 1,
  multiplierFreezer = 1,
  portionsEat = 0,
  portionsFreezer = 0,
}: {
  ingredient: Ingredient
  product: Product
  multiplierEat?: number
  multiplierFreezer?: number
  portionsEat?: number
  portionsFreezer?: number
}) => {
  const quantity = calculateIngredientQuantities({
    quantity: ingredient.quantity,
    multiplierEat,
    multiplierFreezer,
    portionsEat,
    portionsFreezer,
    isFreezable: ingredient.freezer || false,
  })

  if (!shouldDisplayIngredient(quantity)) return null

  return (
    <div className="flex items-center gap-2">
      <div className="min-w-[70px]">
        {num(quantity)} {product.unit?.replace('gram', 'g')}
      </div>
      <div className="font-semibold">{product.display}</div>
      {ingredient.freezer && multiplierFreezer > 0 && (
        <Snowflake className="w-4 h-4 text-blue-500" />
      )}
    </div>
  )
}

/** React component that renders instructions with clickable links */
function Instructions({ instructions }: { instructions: string }) {
  return (
    <div className="text-lg my-2 whitespace-pre-line">
      {instructions
        .split(/(https?:\/\/\S+)/)
        .filter((s) => s.trim())
        .map((s, i) => {
          const match = s.match(/https?:\/\/\S+/)
          if (match) {
            let link = match[0]
            try {
              link = 'Bekijk op ' + new URL(link).hostname
            } catch (error) {}
            return (
              <div className="my-2" key={i}>
                <a
                  href={match[0]}
                  target="_blank"
                  className="hover:underline focus-visible:underline focus:outline-hidden text-lime-700 font-medium underline"
                >
                  {link}
                </a>
              </div>
            )
          }
          return <Fragment key={i}>{s}</Fragment>
        })}
    </div>
  )
}

function MultiplierInput({
  value,
  onChange,
  portionsEat,
}: {
  value: number | null
  onChange: (value: number) => void
  portionsEat: number
}) {
  const multiplier = value ?? 1
  const total = multiplier * portionsEat
  const step = portionsEat % 2 ? 1 : 0.5

  return (
    <div className="flex items-center relative rounded-xl border border-gray-200 shadow-sm">
      <button
        className={
          'p-2 pr-6 rounded-l-xl' +
          (multiplier === 0 ? ' hover:bg-blue-100' : ' hover:bg-green-100')
        }
        type="button"
        onClick={() => {
          onChange(multiplier - step)
        }}
      >
        {multiplier === 0 ? (
          <Snowflake className="text-blue-500" />
        ) : (
          <MinusIcon />
        )}
      </button>
      <button
        className="p-2 pl-6 rounded-r-xl hover:bg-green-100"
        type="button"
        onClick={() => {
          onChange(multiplier + step)
        }}
      >
        {multiplier === 0 ? (
          <LeafyGreenIcon className="text-lime-600" />
        ) : (
          <PlusIcon />
        )}
      </button>
      <div className="absolute pointer-events-none leading-none inset-0 text-xs font-medium flex flex-col items-center justify-center">
        <div className="font-bold text-lg leading-none">{total}</div>
      </div>
    </div>
  )
}

function FreezerMultiplierInput({
  value,
  onChange,
  recipe,
  invert,
}: {
  value: number | null
  onChange: (value: number) => void
  recipe?: FullRecipe
  invert?: boolean
}) {
  const actual = value ?? 1
  const total = actual * (recipe?.portionsFreezer || 1)
  const step = recipe?.portionsFreezer
    ? (recipe?.portionsFreezer || 0) % 2
      ? 1
      : 0.5
    : 1

  return (
    <div
      className={
        'flex items-center relative rounded-xl border border-gray-200 shadow-sm ' +
        (invert ? 'flex-row-reverse' : '')
      }
    >
      <button
        className={
          'flex-1 flex justify-center p-2 hover:bg-blue-100 ' +
          (invert ? 'pl-6 rounded-r-xl' : 'pr-6 rounded-l-xl')
        }
        type="button"
        onClick={() => {
          onChange(actual - step)
        }}
      >
        <MinusIcon />
      </button>
      <button
        className={
          'flex-1 flex justify-center p-2 hover:bg-blue-100 ' +
          (invert ? 'pr-6 rounded-l-xl' : 'pl-6 rounded-r-xl')
        }
        type="button"
        onClick={() => {
          onChange(actual + step)
        }}
      >
        <PlusIcon />
      </button>
      <div className="absolute pointer-events-none leading-none inset-0 text-xs font-medium flex flex-col items-center justify-center">
        <div className="font-bold text-lg leading-none flex items-center gap-1">
          {total}
          <Snowflake className="w-4 h-4 text-blue-500" />
        </div>
      </div>
    </div>
  )
}
